import Icon from "@components/Atoms/Icon";
import { useRouter } from "@core/Api/useRouter";
import { RequestStatus, useApi } from "@core-ui/hooks/useApi";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import { makeGitShareData } from "@ext/git/actions/Clone/logic/makeGitShareData";
import { useCloneRepo } from "@ext/git/actions/Clone/logic/useCloneRepo";
import { ErrorLine, TechnicalDetails } from "@ext/git/actions/RepositoryBroken/TechnicalDetails";
import getUrlFromShareData from "@ext/git/core/GitPathnameHandler/clone/logic/getUrlFromShareData";
import type GitStorageData from "@ext/git/core/model/GitStorageData";
import t from "@ext/localization/locale/translate";
import useStorage from "@ext/storage/logic/utils/useStorage";
import { AlertConfirm } from "@ui-kit/AlertDialog";
import { Button } from "@ui-kit/Button";
import { Dialog, DialogBody, DialogContent, DialogTitle, DialogTrigger } from "@ui-kit/Dialog";
import { useMemo, useState } from "react";

export type RepositoryNotFullyClonedProps = {
	trigger: JSX.Element;
	error: Error;
};

export const RepositoryNotFullyCloned = ({ trigger, error }: RepositoryNotFullyClonedProps) => {
	const router = useRouter();
	const { name, link } = useCatalogPropsStore(
		(state) => ({ name: state.data?.name, link: state.data?.link.pathname }),
		"shallow",
	);

	const {
		call: removeCatalog,
		status: removeStatus,
		error: removeCatalogError,
	} = useApi({
		url: (api) => api.removeCatalog(),
		onDone: () => {
			setOpen(false);
			void router.pushPath("/");
		},
	});

	const source = useStorage();

	const url = useMemo(() => getUrlFromShareData(makeGitShareData(link)), [link]);

	const { startClone } = useCloneRepo({
		storageData: {
			name: name,
			url,
			source,
		} as GitStorageData,
		deleteIfExists: true,
		skipCheck: true,
		redirectOnClone: link,
		onStart: () => {
			setOpen(false);
			void router.pushPath("/");
		},
	});

	const isBusy = removeStatus === RequestStatus.Loading;

	const [open, setOpen] = useState(false);

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger asChild>{trigger}</DialogTrigger>
			<DialogContent>
				<DialogBody className="lg:py-6">
					<div className="flex flex-row items-start gap-4">
						<Icon className="text-status-error" code="circle-alert" size="24px" />
						<div className="min-w-0 flex-1 space-y-2">
							<DialogTitle className="text-lg">{t("git.error.broken.clone-failed.title")}</DialogTitle>
							<p style={{ paddingBottom: "1rem" }}>{t("git.error.broken.clone-failed.body")}</p>
							<ErrorLine error={removeCatalogError} />
						</div>
					</div>
				</DialogBody>
				<div className="flex gap-2 pb-4 pl-14 pr-4 lg:pl-16">
					<TechnicalDetails className="my-auto text-sm" error={error}>
						{t("git.error.broken.clone-failed.technical-details")}
					</TechnicalDetails>
					<div className="ml-auto gap-2 flex">
						<AlertConfirm
							description={t("git.error.broken.clone-failed.delete.description")}
							onConfirm={removeCatalog}
							title={t("git.error.broken.clone-failed.title")}
						>
							<Button
								className="ml-auto text-sm"
								disabled={isBusy}
								iconClassName={isBusy ? "animate-spin" : ""}
								startIcon={isBusy ? "loader-circle" : null}
								variant="outline"
							>
								{t("git.error.broken.clone-failed.delete.button")}
							</Button>
						</AlertConfirm>
						<AlertConfirm
							description={t("git.error.broken.clone-failed.clone.description")}
							onConfirm={startClone}
							title={t("git.error.broken.clone-failed.clone.title")}
						>
							<Button className="text-sm" variant="primary">
								{t("git.error.broken.clone-failed.clone.button")}
							</Button>
						</AlertConfirm>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
